import type { AdvisorNotification } from "@repo/shared/types";
import { Bell } from "lucide-react";
import { useCallback, useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationArrivals,
  useNotificationList,
  useUnreadNotifications,
} from "@/lib/hooks/use-notifications";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./notification-bell.i18n";
import { NotificationItem } from "./notification-item";
import { useNotificationText } from "./use-notification-text";

/**
 * QL-728 — the top bar's bell: unread count, the newest notifications, and a
 * toast when a new one arrives while this tab is visible. Driven by the 15 s
 * unread poll; there is no push channel to the browser.
 */
export function NotificationBell() {
  const t = useT(T);
  const describe = useNotificationText();
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const { data: unread, dataUpdatedAt } = useUnreadNotifications(true);
  const list = useNotificationList(open);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const count = unread?.count ?? 0;

  // Toast each notification that arrives while this tab is visible.
  const toastArrival = useCallback(
    (n: AdvisorNotification) => {
      const text = describe(n);
      toast(text.title, { description: text.detail });
    },
    [describe],
  );
  useNotificationArrivals(unread?.latest, dataUpdatedAt, toastArrival);

  const openItem = (n: AdvisorNotification) => {
    setOpen(false);
    if (n.readAt === null) markRead.mutate(n.id);
  };

  const items = list.data?.items ?? [];

  return (
    <>
      {/* The badge is visual; say a change in the count to screen readers too. */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {count > 0 ? t.bellLabel(count) : ""}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" tooltip={t.bellLabel(count)}>
            <Bell className="h-5 w-5" />
            {count > 0 && (
              <span
                aria-hidden
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold leading-none text-white"
              >
                {count > 99 ? "99+" : count}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0" role="dialog" aria-labelledby={titleId}>
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 id={titleId} className="text-sm font-semibold">
              {t.title}
            </h2>
            {count > 0 && (
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0"
                loading={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                {t.markAllRead}
              </Button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto p-1">
            {list.isError ? (
              <p className="px-3 py-6 text-center text-sm text-destructive">{t.loadFailed}</p>
            ) : list.isPending ? null : items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t.empty}</p>
            ) : (
              <ul>
                {items.map((n) => (
                  <li key={n.id}>
                    <NotificationItem notification={n} onOpen={openItem} />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t px-4 py-2">
            <Link
              to="/settings/notifications"
              className="text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setOpen(false)}
            >
              {t.settings}
            </Link>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
